import _ from 'lodash';

import { Model } from 'backbone';

const PLAYING = 'playing';
const NOT_PLAYING = 'not-playing';
const INJURED = 'injured';
const UNCERTAIN = 'uncertain';
const INTERCHANGE = 'interchange';
const EMERGENCY = 'emergency';
const RESERVE = 'reserve';
const SUSPENDED = 'suspended';

const MIN_PRICE = 230000;

const
	AVAILABLE = new Set([PLAYING, UNCERTAIN]),
	SHOULD_PLAY = new Set([PLAYING, RESERVE, EMERGENCY]),
	silent = { silent: true },
	average = arr => Math.round10(_.divide(_.sum(arr), _.size(arr)), -2);

export class Player extends Model {

	static get all_statuses() {
		return [
			PLAYING,
			NOT_PLAYING,
			INJURED,
			UNCERTAIN,
			INTERCHANGE,
			EMERGENCY,
			RESERVE,
			SUSPENDED
		];
	}

	get match_keys() {
		return {
			TOG: 0, /*Time On Ground*/
			FDO: 2, /*Forced Drop Out*/
			T: 8, /*Tries*/
			TS: 5, /*Try Saves*/
			G: 2, /*Goals*/
			FG: 5, /*Field Goals*/
			TA: 5, /*Try Assists*/
			LB: 4, /*Line Breaks*/
			LBA: 2, /*Line Break Assists*/
			TCK: 1, /*Tackles*/
			TB: 2, /*Tackle Breaks*/
			MT: -2, /*Missed Tackles*/
			OF: 2, /*Offloads*/
			OFG: 2, /*Ground Offloads*/
			OFH: 4, /*Hand Offloads*/
			ER: -2, /*Errors*/
			TO: 4, /*Turnovers*/
			FTF: 4, /*40/20/40's*/
			MG: 0.1, /*Total Metres gained*/
			KM: (0.1/3), /*Kick Metres*/
			KD: 1, /*Kicks Defused*/
			PC: -2, /*Penalties conceded*/
			SB: -5, /*Sin-Bin*/
			SO: -10, /*Send-Off*/
			SAI: -1, /*Six Again Infringments*/
			EFIG: 2, /*Escape From In Goal*/
		}
	}

	getFullName() {
		return `${this.get('first_name')} ${this.get('last_name')}`;
	}

	isAvailable() {
		return AVAILABLE.has(this.get('status'));
	}

	isShouldPlay() {
		return SHOULD_PLAY.has(this.get('status'));
	}

	setPositionsName({ positions_by_id }) {

		return this.setPositionNames({ positions_by_id });
	}

	setPositionNames({ positions_by_id, key = 'short_name' }) {
		const position_names = this.get('positions').map(
			id => _.result(positions_by_id, `${id}.${key}`)
		);

		this.set({ position_names }, silent);

		return this;
	}

	setStatsCostDividedByTotalPoints() {
		const
			cost = this.get('cost'),
			stats = this.get('stats'),
			total_points = stats.total_points,
			total_points_handled = total_points <= 0 ? 1 : total_points,
			cost_divided_by_points = parseInt(total_points ? (cost / total_points_handled) : 0, 10),
			result = {
				stats: {
					...stats,
					cost_divided_by_points
				}
			};

		this.set(result, silent);

		return this;
	}

	setStatsCostDividedByAvgPoints() {
		const
			cost = this.get('cost'),
			stats = this.get('stats'),
			total_points = stats.total_points,
			avg_points = stats.avg_points,
			cost_divided_by_avg_points = parseInt(total_points ? (cost / avg_points) : 0, 10),
			result = {
				stats: {
					...stats,
					cost_divided_by_avg_points
				}
			};

		this.set(result, silent);

		return this;
	}

	setFavourite({ favourites = [] }) {
		let result = {
			is_favourite: favourites.includes(this.get('id'))
		};

		this.set(result, silent);

		return this;
	}

	setByRound({ bye_rounds }) {
		const
			squad_id = this.get('squad_id'),
			bye_round = _.find(
				bye_rounds,
				({ bye_squads }) => bye_squads.includes(squad_id)
			);

		if (bye_round) {
			this.set({
				stats: {
					...this.get('stats'),
					bye_round_id: bye_round.id
				}
			}, silent);
		}
	}

	/*scheduled_round_id,*/
	// eslint-disable-next-line complexity
	setCoachStats({ coach, actual_round_id, last_round_id, leader_id = 0 }) {

		const
			player_id = this.get('id'),
			coach_stats = _.result(coach, player_id, {});

		const { proj_scores, proj_prices, break_evens } = coach_stats,
			round_proj_score = _.result(proj_scores, actual_round_id, 0);

		let proj_score = this.isAvailable() ? round_proj_score : 0;
		// let proj_score = round_proj_score;

		if (leader_id===player_id && this.isAvailable()) {
			proj_score *= 2;
		}

		const stats = this.get('stats');

		const
			proj_price_next_round = _.result(proj_prices, actual_round_id + 1, 0),
			proj_price_change_next_round = proj_price_next_round - this.get('cost'),
			proj_price_change_next_two_rounds = (
				_.result(proj_prices, actual_round_id + 2, 0) - proj_price_next_round
			),
			in_20_avg_percent = Math.round10(
				_.divide(coach_stats.in_20_avg / stats.games_played) * 100, 0
			),
			out_20_avg_percent = Math.round10(
				_.divide(coach_stats.out_20_avg / stats.games_played) * 100, 0
			);
		const ppm = Math.round10(_.divide(stats.avg_points, stats.tog), -2);

		this.set({
			stats: {
				...stats,
				...coach_stats,

				points_2_rounds_prior: this.getPlayerScoreByRound(last_round_id - 1, 'DNP'),
				points_3_rounds_prior: this.getPlayerScoreByRound(last_round_id - 2, 'DNP'),

				last_3_avg_vs_proj: Math.round10(
					stats.last_3_avg - coach_stats.last_3_proj_avg, -2
				),
				last_3_avg_vs_season: Math.round10(
					stats.last_3_avg - stats.avg_points, -2
				),

				// This functionality was re-written 4 time,
				// that's why it has different name, but the same data.
				// Also it used in a lot of different places.
				break_even: _.result(break_evens, actual_round_id, 0),
				break_even_one_round_after_current:(
					_.result(break_evens, actual_round_id, 0)
				),
				break_even_two_rounds_after_current: (
					_.result(break_evens, actual_round_id + 1, 0)
				),

				// This functionality was re-written 4 time,
				// that's why it has different name, but the same data.
				// Also it used in a lot of different places.
				proj_score,
				proj_score_one_round_after_current: (
					_.result(proj_scores, actual_round_id, 0)
				),
				proj_score_two_rounds_after_current: (
					_.result(proj_scores, actual_round_id + 1, 0)
				),
				proj_score_avg_for_3_rds: average(
					_.values(_.pick(proj_scores, _.range(actual_round_id, actual_round_id + 3)))
				),

				proj_price_change_next_round,
				proj_price_change_next_two_rounds,
				last_3_avg_price_changes: average(this.last_3_price_change),
				in_20_avg_percent: _.isFinite(in_20_avg_percent) ? `${in_20_avg_percent}%` : '-',
				out_20_avg_percent: _.isFinite(out_20_avg_percent) ? `${out_20_avg_percent}%` : '-',
				ppm: isNaN(ppm) ? '-' : ppm
			}
		}, silent);

		this.setLastTransfersData();

		return this;
	}

	get last_3_price_change() {
		const arr = _.chain(_.result(this.get('stats'), 'prices', {}))
			.values()
			.reverse()
			.take(4)
			.map((price, index, prices) => price - _.result(prices, index + 1, 0))
			.take(3)
			.value();

		return _.size(arr) < 2 ? [0] : arr;
	}

	setFutureGamesStats({ rounds, coach, squad_by_id, venues_by_id, actual_round_id }) {
		const
			coach_stats = _.result(coach, this.get('id')),
			getValueFrom = _.curry((db, key, default_val) => _.result(db, key, default_val)),
			// byScheduled = _.matches({ status: 'scheduled' }),
			getMatches = _.partial(_.result, _, 'matches'),
			isMatchContainSquadId = _.curry((squad_id, match) => (
				_.at(match, ['home_squad_id', 'away_squad_id']).includes(squad_id)
			)),
			filterByPlayerSquad = _.partial(
				_.filter, _, isMatchContainSquadId(this.get('squad_id'))
			),
			getOpponentByPlayerSquadId = _.curry((squad_id, squad_by_id, match) => {
				const opponent_id = _(match)
					.at(['home_squad_id', 'away_squad_id'])
					.without(squad_id).first();

				return _.result(squad_by_id, opponent_id, {});
			}),
			getPredictGamesStats = (getStats, getOpponentFromMatch, getVenue, match) => {
				const
					{ venue_id, round } = match,
					venue = getVenue(venue_id, {}),
					opponent = getOpponentFromMatch(match, {});

				return {
					round, venue, opponent,
					avg_opposition: getStats(`opponents.${opponent.id}`, '-'),
					avg_at_venue: getStats(`venues.${venue_id}`, '-')
				}
			};

		const future_games = _(rounds)
			.filter(round => round.id >= actual_round_id)
			.take(3)
			.map(_.flow([
				getMatches,
				filterByPlayerSquad
			]))
			.flatten()
			.map(_.partial(
				getPredictGamesStats,
				getValueFrom(coach_stats),
				getOpponentByPlayerSquadId(this.get('squad_id'), squad_by_id),
				getValueFrom(venues_by_id),
			))
			.value();

		const
			names = [
				'one_round_after_current',
				'two_rounds_after_current',
				'three_rounds_after_current'
			],
			getAvgOpposition = _.partial(_.result, _, 'avg_opposition'),
			getAvgVenues = _.partial(_.result, _, 'avg_at_venue'),
			avg_oppositions = future_games.map(getAvgOpposition).filter(_.isNumber),
			avg_venues = future_games.map(getAvgVenues).filter(_.isNumber),
			opp_avg_rounds = future_games.reduce((acc, match, index) => {
				acc[`opp_avg_${names[index]}`] = match.avg_opposition;
				return acc;
			}, {}),
			venues_avg_rounds = future_games.reduce((acc, match, index) => {
				acc[`venue_avg_${names[index]}`] = match.avg_at_venue;
				return acc;
			}, {});

		this.set({
			stats: {
				...this.get('stats'),
				future_games,
				...opp_avg_rounds,
				...venues_avg_rounds,
				opp_avg_3_rounds: average(avg_oppositions),
				venue_avg_3_rounds: average(avg_venues),
			}
		}, silent);

		return this;
	}

	setSquadName({ squad_by_id }) {
		const
			squad_id = this.get('squad_id'),
			squad_name = _.result(squad_by_id, `${squad_id}.name`);

		this.set({ squad_name }, silent);

		return this;
	}

	setCostChanges(args) {
		return this.setSeasonCostChanges()
			.setRoundCostChanges(args);
	}

	setSeasonCostChanges() {
		const round_id = _.first(
			_.keys(
				_.result(this.attributes, 'stats.prices', {})
			)
		);
		return this.setRoundCostChanges({
			round_id,
			filed_name: 'cost_season_diff'
		});
	}

	setRoundCostChanges({ round_id = 1, filed_name = 'cost_round_diff' } = {}) {
		const
			price = _.result(this.attributes, `stats.prices[${round_id}]`, this.get('cost')),
			diff = (this.get('cost') - price) || '-';

		this.set({ [filed_name]: diff }, silent);

		return this;
	}

	setOpponent({ squad_by_id, round: { matches = [], bye_squads = [] } }) {
		const
			bySquadId = match => {
				const squads_id = _.at(match, ['home_squad_id', 'away_squad_id']);

				return squads_id.includes(this.get('squad_id'));
			},
			is_bye = bye_squads.includes(this.get('squad_id')),
			{ home_squad_id, away_squad_id } = _.find(matches, bySquadId) || {};

		const
			player_suqad_id = this.get('squad_id'),
			opponents = {
				home: _.result(squad_by_id, home_squad_id, {}),
				away: _.result(squad_by_id, away_squad_id, {})
			},
			opponent_object = _.omitBy(opponents, { id: player_suqad_id }),
			opponent = _.first(_.values(opponent_object)),
			squad_object = _.omitBy(opponents, opponent),
			squad = _.first(_.values(squad_object)) || {},
			is_at_home = _.keys(opponent_object).includes('home');

		if (is_bye) {
			opponents.symbol = 'BYE';
		}
		else {
			opponents.symbol = is_at_home ? '@' : 'v';
		}

		this.set({
			is_bye,
			squad,
			opponents,
			opponent: {
				...opponent,
				is_at_home,
			}
		}, silent);

		return this;
	}

	setVenue({ venues_by_id, round: { matches } }) {
		const
			bySquadId = match => {
				const squads_id = _.at(match, ['home_squad_id', 'away_squad_id']);

				return squads_id.includes(this.get('squad_id'));
			},

			{ venue_id } = _.find(matches, bySquadId) || {};

		this.set({
			venue: _.result(venues_by_id, venue_id)
		}, silent);

		return this;
	}

	/* eslint-disable complexity */
	setLockedFields({
		is_unlimited_trades,
		is_active_round,
		captain,
		vice_captain,
		week_trades_left,
		is_endseason
	}) {
		const
			player_id = parseInt(this.get('id'), 10),
			is_leader = [captain, vice_captain].includes(player_id),
			is_leader_locked = is_leader && is_active_round,
			is_captain_locked = !(captain && is_active_round),
			is_vice_captain_locked = !(vice_captain && is_active_round),
			is_locked = this.is_locked || is_leader_locked,
			is_removable = is_unlimited_trades,
			can_be_trade_out = !is_removable && week_trades_left > 0;

		this.set({
			is_locked,
			is_removable,
			can_be_captain: is_captain_locked && !is_endseason,
			can_be_vice_captain: is_vice_captain_locked && !is_endseason,
			can_be_trade_out: can_be_trade_out && !is_endseason,
		}, silent);

		return this;
	}

	updatePlayerStatus() {
		this.set({
			status: this.get('is_bye') ? 'is-bye' : this.get('status')
		}, silent);

		return this;
	}

	setStatsPointsLastRound(
		{ is_preseason = true, rounds = [], round_id = 1, leader_id, is_return = false }
	) {
		this.updatePlayerStatus();

		const
			score = this.getPlayerScoreByRound(round_id),
			has_score = !_.isUndefined(score),
			round = _.find(rounds, ({ id }) => id===round_id),
			player_squad_match = this.getPlayerSquadMatch(round),
			is_match_active = this.isPlayerSquadPlayingNow(player_squad_match),
			is_match_scheduled = this.isPlayerSquadScheduled(player_squad_match),
			is_not_scored_yet = this.isNotScoredYet({
				is_match_scheduled, is_match_active, is_preseason, has_score
			}),
			is_bye = _.result(round, 'bye_squads', []).includes(this.get('squad_id'));
		let points_last_round = is_bye ? 'BYE' : this.getPointLastRound({
			is_not_scored_yet,
			score
		});

		const points_last_round_original = points_last_round;

		if (leader_id===this.get('id') && _.isNumber(points_last_round)) {
			points_last_round *= 2;
		}

		if (is_return) {
			return points_last_round;
		}
		this.set({
			is_playing_now: is_match_active,
			is_dnp: this._isNotPlay(points_last_round),
			stats: {
				...this.get('stats'),
				points_last_round,
				points_last_round_original,
			}
		}, silent);

		return this;
	}

	_isNotPlay(points_val) {
		return _.eq(points_val, 'DNP') || _.eq(points_val, 'BYE');
	}

	getPointLastRound({ is_not_scored_yet, score = 'DNP' }) {
		return is_not_scored_yet ? '' : score;
	}

	isNotScoredYet({ is_preseason, has_score, is_match_active, is_match_scheduled }) {
		return is_preseason || is_match_scheduled || (is_match_active && !has_score);
	}

	isPlayerSquadPlayingNow(match) {
		return _.result(match, 'status')==='playing';
	}

	isPlayerSquadScheduled(match) {
		return _.result(match, 'status')==='scheduled';
	}

	getPlayerScoreByRound(round_id, default_val) {
		if (round_id < 1) {
			default_val = '-';
		}

		return _.result(this.attributes, `stats.scores[${round_id}]`, default_val);
	}

	getPlayerSquadMatch(round) {
		const
			squad_id = this.get('squad_id'),
			matches = _.result(round, 'matches', []);
		return _.find(matches, ({ home_squad_id, away_squad_id }) => {
			return [home_squad_id, away_squad_id].includes(squad_id);
		});
	}

	isPlayerScored(args) {
		return this.alreadyPlayed(args) && this.hasScores(args);
	}

	hasScores({ round, is_preseason = false }) {
		if (is_preseason) {
			return false;
		}

		return !_.isUndefined(this.getPlayerScoreByRound(round.id));
	}

	alreadyPlayed({ round, is_preseason = false }) {
		const
			has_score = this.hasScores({ round, is_preseason }),
			player_squad_match = this.getPlayerSquadMatch(round),
			is_match_active = this.isPlayerSquadPlayingNow(player_squad_match),
			is_match_scheduled = this.isPlayerSquadScheduled(player_squad_match);

		return !this.isNotScoredYet({
			is_match_scheduled, is_match_active, is_preseason, has_score
		});
	}

	setMatchStats({ match_stats, headings }) {
		const player_stats = _.result(match_stats, this.get('id')),
			stats = {
				match_stats: {},
				match_points: {}
			},
			keys = this.match_keys
		;

		_.each(player_stats, function(stat, key) {
			const factor = keys[key];
			stats.match_stats[key] = stat;
			stats.match_points[key] = stat * factor;

			if (key==='MG' || key==='KM') {
				stats.match_points[key] = Math.floor(stats.match_points[key]);
			}
			if(key==='OF'){
				const offloadG = player_stats['OFG'] * 2;
				const offloadH = player_stats['OFH'] * 4;

				const calculatedOffloads = offloadG + offloadH;
				stats.match_points['OF'] = calculatedOffloads;
			}
		});

		this.set({
			...stats,
			headings
		});

		return this;
	}

	getMatchStats({ match_stats }) {
		const stats = {
				match_stats: {},
				match_points: {}
			},
			keys = this.match_keys
		;

		_.each(match_stats, function(stat, key) {
			const factor = keys[key];
			stats.match_stats[key] = stat;
			stats.match_points[key] = stat * factor;

			if (key==='MG' || key==='KM') {
				stats.match_points[key] = Math.floor(stats.match_points[key]);
			}
		});

		return stats;
	}

	setFixturesTabData({ rounds, venues, squads, coach_stats }) {
		const fixtures = this.getSeasonFixtures({ rounds, venues, squads });
		const upcoming_fixtures = this.getUpcomingFixtures({ rounds, venues, squads, coach_stats });

		this.set({
			fixtures,
			upcoming_fixtures
		});

		return this;
	}

	getSquadIdToUse({ round_id }){
		const transfer_round_id = this.get("transfer_round");
		const squad_id = this.get('squad_id');
		const original_squad_id = this.get("original_squad_id");

		return transfer_round_id <= round_id ? squad_id : original_squad_id;
	}

	getSeasonFixtures({ rounds, venues, squads }) {
		const non_scheduled_rounds = rounds.non_scheduled_rounds,
			methods = _.bindAll(this, [
				'getVenueOpponent',
				'setStatsPointsLastRound',
				'getSquadIdToUse'
			]),
			player_attrs = this.attributes
		;

		let total_score = 0,
			games_played = 0
		;

		// eslint-disable-next-line complexity
		return _.map(non_scheduled_rounds, function(round) {
			const round_id = round.get('id');
			const squad_id_to_use = methods.getSquadIdToUse({ round_id })
			const match = round.getPlayerMatch(squad_id_to_use);


			if (!match.id) {
				return {
					round_id,
					is_bye: true
				}
			}
			let avg_score = 0;

			const {
				opponent_id, opponent, venue
			} = methods.getVenueOpponent({ match, venues, squads });

			const score = methods.setStatsPointsLastRound(
					{ is_preseason: false, rounds: rounds.toJSON(), round_id, is_return: true }
				),
				future_round_id = round_id + 1,
				price = _.result(player_attrs, `stats.prices.${round_id}`),
				future_price = _.result(player_attrs, `stats.prices.${future_round_id}`, 0),
				price_change = future_price ? future_price - price : '-';

			let price_change_percent = future_price ? ((future_price - price) / price) * 100 : '-';

			if (_.isNumber(price_change_percent)) {
				price_change_percent = price_change_percent.toFixed(2);
			}

			if (_.isNumber(score)) {
				total_score += score;
				++games_played;
			}

			if (games_played > 0) {
				avg_score = (total_score / games_played).toFixed(2);
			}


			return {
				round_id,
				opponent_id,
				opponent,
				venue,
				score,
				total_score,
				avg_score,
				games_played,
				price,
				price_change,
				price_change_percent,
				is_bye: false
			}

		});
	}



	getUpcomingFixtures({ rounds, venues, squads, coach_stats }) {
		let
			scheduled_rounds = rounds.scheduled_rounds,
			active_round = rounds.active,
			methods = _.bindAll(this, [
				'getVenueOpponent',
				'setStatsPointsLastRound',
				'getSquadIdToUse'
			])
		;

		if (active_round) {
			scheduled_rounds = [
				active_round,
				...scheduled_rounds
			];
		}

		if (coach_stats) {
			coach_stats = coach_stats.toJSON();
		}

		return _.map(scheduled_rounds, (round, index) => {

			const round_id = round.get('id');
			const squad_id_to_use = methods.getSquadIdToUse({ round_id })
			const match = round.getPlayerMatch(squad_id_to_use)


			if (!match.id) {
				return {
					round_id,
					is_bye: true
				}
			}

			const {
					opponent_id, opponent, venue
				} = methods.getVenueOpponent({ match, venues, squads }),
				date = match.date,
				future_round_id = round_id + 1,
				score = methods.setStatsPointsLastRound(
					{ is_preseason: false, rounds: rounds.toJSON(), round_id, is_return: true }
				)
			;
			let coach_data = {};

			if (coach_stats) {

				let opp_data = _.result(coach_stats, `opponents.${opponent_id}`, {}),
					venue_data = _.result(coach_stats, `opponents.${match.venue_id}`, {}),
					proj_price = _.result(coach_stats, `proj_prices.${round_id}`, 0),
					future_proj_price = _.result(
						coach_stats, `proj_prices.${future_round_id}`, '-'
					);
				const is_future_proj_price = _.isNumber(future_proj_price);
				let proj_price_change = '-',
					proj_price_change_percent = '-';

				if (is_future_proj_price) {
					const price = _.result(coach_stats, `stats.prices.${round_id}`, false);
					const current_price = !index ? this.get('cost') : price;
					const proj_price_change_diff = future_proj_price - proj_price;

					proj_price_change = future_proj_price - (current_price || proj_price);
					proj_price_change_percent = (proj_price_change_diff / proj_price) * 100;
				}


				if (_.isNumber(proj_price_change_percent)) {
					proj_price_change_percent = proj_price_change_percent.toFixed(2);
				}

				coach_data = {
					opp_games: _.result(opp_data, 'games', '-'),
					opp_avg: _.result(opp_data, 'avg', '-'),
					venue_games: _.result(venue_data, 'games', '-'),
					venue_avg: _.result(venue_data, 'avg', '-'),
					proj_score: _.result(coach_stats, `proj_scores.${round_id}`, ''),
					break_even: _.result(coach_stats, `break_evens.${round_id}`, ''),
					proj_price,
					proj_price_change,
					proj_price_change_percent,
					be_pct: _.result(coach_stats, `be_pct.${round_id}`, '-')
				}

			}


			return {
				round_id,
				score,
				opponent_id,
				opponent,
				venue,
				date,
				is_bye: false,
				...coach_data
			}
		});
	}

	getVenueOpponent({ match, venues, squads = null }) {
		let methods = _.bindAll(this, [
			'getSquadIdToUse'
		])
		const { home_squad_id, away_squad_id } = match,
			round_id = match.round,
			squad_id = methods.getSquadIdToUse({ round_id }),
			opponent_id = _.first(_.without([home_squad_id, away_squad_id], squad_id)),
			venue_model = venues.get(match.venue_id),
			opponent_model = squads ? squads.get(opponent_id) : {},
			opponent = _.result(opponent_model, 'attributes.name', ''),
			full_venue_name = _.result(venue_model, 'attributes.name', '')
		;

		let venue = _.result(venue_model, 'attributes.short_name', full_venue_name);


		if (venue==='') {
			venue = full_venue_name;
		}

		return {
			opponent_id, opponent, venue
		}
	}

	setMatchStatsTabData({ match_stat, rounds, venues }) {
		let methods = _.bindAll(this, ['getMatchStats', 'getVenueOpponent', 'getSquadIdToUse']);

		const player_attrs = this.attributes;

		let player_match_stats = _.map(
			match_stat.attributes, function(stat, round_id) {
				if (round_id!=='all') {
					round_id = parseInt(round_id, 10);
				}

				const match_stats = methods.getMatchStats({ match_stats: stat }),
					data = {
						round_id,
						match_stats
					}
				;

				if (round_id==='all') {
					return {
						...data,
						score: _.result(player_attrs, 'stats.total_points')
					};
				}

				const round = rounds.get(round_id),
					squad_id = methods.getSquadIdToUse({ round_id }),
					match = round.getPlayerMatch(squad_id);

				if (!match.id) {
					return {
						round_id,
						is_bye: true
					}
				}

				const {
					opponent_id, venue
				} = methods.getVenueOpponent({ match, venues });

				return {
					...data,
					opponent: opponent_id,
					venue,
					score: _.result(player_attrs, `stats.scores.${round_id}`)
				};
			}
		);

		this.set({
			player_match_stats
		});

		return this;
	}

	setOpponentsTabData({ rounds, venues, squads, coach_stats }) {
		let methods = _.bindAll(this, ['getOpponentAvgPoints', 'getOpponentVenues']);
		const squad_id = this.get('squad_id');

		const opponents_tab = _.map(coach_stats.get('opponents'), function(opponent, id) {
			id = parseInt(id, 10);

			const squad = squads.get(id),
				opponent_name = squad ? squad.get('full_name') : '-',
				{ last_3_score, last_3_avg } = methods.getOpponentAvgPoints(opponent),
				{ games, avg } = opponent,
				opp_venues = methods.getOpponentVenues({ rounds, venues, id, squad_id })
			;
			return {
				id,
				opponent_name,
				last_3_score,
				last_3_avg,
				games,
				avg,
				opp_venues
			}
		});

		this.set({
			opponents_tab
		});
	}

	getOpponentAvgPoints(opponent) {
		let { last_3 } = opponent,
			size = _.size(last_3),
			{ last_3_avg = 0, last_3_score = [] } = {},
			expected_size = 3
		;

		last_3_score = _.map(last_3, function(score) {
			last_3_avg += score;

			return score;
		});

		if (size===expected_size) {
			last_3_avg = Math.round10(last_3_avg / 3);
		}
		else {
			last_3_score = _.concat(_.fill(Array(expected_size - size), 'NA'), last_3_score);
			last_3_avg = last_3_avg / size;

		}

		return {
			last_3_score,
			last_3_avg
		};
	}

	getOpponentVenues({ rounds, venues, id, squad_id }) {
		let opp_venues = [];
		rounds.each(function(round) {
			const match = round.getOpponentVenues({ venues, id, squad_id });

			if (match.id) {
				const round_id = round.get('id');
				const venueObj = venues.get(match.venue_id)
				const venue = venueObj.get('name');


				opp_venues.push({
					round_id,
					venue
				});

			}
		});

		return opp_venues;
	}

	setVenuesTabData({ venues, coach_stats }) {
		let methods = _.bindAll(this, ['getOpponentAvgPoints']);

		const venues_tab = _.map(coach_stats.get('venues'), function(venue_data, id) {
			id = parseInt(id, 10);

			const venue = venues.get(id),
				venue_name = venue ? venue.get('name') : '-',
				{ last_3_score, last_3_avg } = methods.getOpponentAvgPoints(venue_data),
				{ games, avg, low, high } = venue_data
			;

			return {
				id,
				venue_name,
				last_3_score,
				last_3_avg,
				games,
				avg,
				low,
				high
			}
		});

		this.set({
			venues_tab
		});
	}

	get is_locked() {
		return parseInt(this.get('locked'), 10)===1;
	}

	getProjPricesObject({ avg, next_3_rounds, is_actual_active, actual_round_id }) {
		const
			prices = {},
			{ cost, stats } = this.attributes,
			{ proj_scores = {}, scores } = stats,
			proj_wpr_koeficient = [1/3, 1/3.75, 1/5, 1/7.5, 1/15];
		let
			price = cost,
			wpr = stats.wpr;

		const setRoundPrice = ({ round_id, round_price, diff = 0 }) => (
			prices[round_id] = { id: round_id, price: round_price, diff }
		);

		_.each(next_3_rounds, (round_id, index) => {
			if (proj_scores[round_id] === 0) {
				setRoundPrice({ round_id, round_price: price });
				return ;
			}

			const last_5 = _.range(index + 1).fill(avg);

			_.forEachRight(
				_.filter(scores, (score, id) => {
					return score && !(is_actual_active && parseInt(id, 10) === actual_round_id);
				}),
				score => _.lt(_.size(last_5), 5) && last_5.push(score)
			);
			while (_.lt(_.size(last_5), 5)) {
				last_5.push(wpr)
			}

			const
				proj_wpr_koef = _.reduce(
					last_5, (acc, score, index) => acc + score * proj_wpr_koeficient[index], 0
				),
				proj_wpr =  Math.round10(0.75 * wpr + 0.25 * proj_wpr_koef, -2),
				proj_price = Math.round10((proj_wpr / wpr) * price, 1);


			const real_proj_price = proj_price < MIN_PRICE ? MIN_PRICE : proj_price;

			setRoundPrice({
				round_id, round_price: real_proj_price, diff:
				Math.round10(real_proj_price, 3) - price
			});

			wpr = proj_wpr;
			price = real_proj_price;
		});

		return prices;
	}

	setLastTransfersData() {
		const stats = this.get('stats');
		const { transfers } = stats;

		let last_transfers_in = 0;
		_.each(transfers, item => last_transfers_in += item.in);

		let last_transfers_out = 0;
		_.each(transfers, item => last_transfers_out += item.out);

		this.set({
			stats: {
				...stats,
				last_transfers_in,
				last_transfers_out
			}
		});
	}
}

export default Player;